import { get } from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";

import { TextButton } from "./Button";
import { Icon } from "./Icon";
import { isTablet, media } from "./common";
import { theme } from "./theme";

export interface Props {
  children: React.ReactNode;
  visibleHeight: number;
  showMoreLabel: string;
  hideMoreLabel: string;
  shadow?: boolean;
  dropdownOn?: boolean;
  mobileVisibleHeight?: number;
  fullWidth?: boolean;
  isGrayShadow?: boolean;
}

export interface State {
  dropdownOn: boolean;
  contentHeight: number;
}

interface WrapperProps {
  visibleHeight?: number;
  mobileVisibleHeight?: number;
  contentHeight?: number;
  dropdownOn: boolean;
  style?: object;
  shadow?: boolean;
  normal?: boolean;
  fullWidth?: boolean;
  isGrayShadow?: boolean;
}

class ShowMore extends React.Component<Props, State> {
  static defaultProps = {
    shadow: true,
  };

  readonly state = {
    contentHeight: 0,
    dropdownOn: !!this.props.dropdownOn,
  };

  private container: any;

  public componentDidMount() {
    this.setContainerHeight();
  }

  public componentDidUpdate() {
    this.setContainerHeight();
  }

  render() {
    const {
      visibleHeight,
      children,
      showMoreLabel,
      hideMoreLabel,
      shadow,
      mobileVisibleHeight,
      fullWidth,
      isGrayShadow,
    } = this.props;
    const { contentHeight, dropdownOn } = this.state;
    const defaultHeight =
      mobileVisibleHeight && isTablet(window.innerWidth)
        ? mobileVisibleHeight
        : visibleHeight;
    const showButton = defaultHeight < contentHeight;
    return (
      <>
        <Container
          className={dropdownOn ? "show-more-open" : "show-more-close"}
          dropdownOn={dropdownOn}
          visibleHeight={Math.min(visibleHeight, contentHeight)}
          mobileVisibleHeight={
            typeof mobileVisibleHeight === "number"
              ? Math.min(mobileVisibleHeight, contentHeight)
              : undefined
          }
          contentHeight={contentHeight}
        >
          <Content ref={(container: any) => (this.container = container)}>
            {children}
          </Content>
        </Container>
        {showButton && (
          <DropDown
            onClick={this.handleClick}
            dropdownOn={dropdownOn}
            shadow={shadow}
            isGrayShadow={isGrayShadow}
            fullWidth={fullWidth}
          >
            {dropdownOn ? hideMoreLabel : showMoreLabel}
            <Icon type={dropdownOn ? "arrow-up" : "arrow-down"} />
          </DropDown>
        )}
      </>
    );
  }

  private handleClick = () => {
    const { dropdownOn } = this.state;
    this.setState({ dropdownOn: !dropdownOn });
  };

  private setContainerHeight = () => {
    const contentHeight = get(this.container, "scrollHeight");
    if (contentHeight && contentHeight !== this.state.contentHeight) {
      this.setState({ contentHeight });
    }
  };
}

export { ShowMore, DropDown };

export default ShowMore;

const Container = styled.div<WrapperProps>`
  height: 0;
  transition: height 0.5s;
  position: relative;
  overflow: hidden;
  margin: 0 -5px;
  padding: 0 5px;

  ${({ visibleHeight, mobileVisibleHeight }) =>
    (visibleHeight || mobileVisibleHeight) &&
    css`
      height: ${mobileVisibleHeight}px;
      ${media.xs`
      height: ${visibleHeight}px;
    `};
    `}
  ${({ dropdownOn, contentHeight }) =>
    dropdownOn &&
    css`
      height: ${contentHeight}px;
      ${media.xs`
      height: ${contentHeight}px;
    `};
    `}
  ${({ dropdownOn, visibleHeight, mobileVisibleHeight }) =>
    !dropdownOn &&
    css`
      height: ${mobileVisibleHeight}px;
      ${media.xs`
      height: ${visibleHeight}px;
    `};
    `}
`;

const DropDown = styled(TextButton)<WrapperProps>`
  position: relative;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  height: auto;
  padding: ${({ fullWidth }) => (fullWidth ? "24px 0 0 0" : "0")};
  line-height: normal;
  text-align: left;
  text-decoration: none;
  color: ${theme.blue};
  outline: none;
  &:hover {
    color: ${theme.blue};
  }
  overflow: visible;
  ${({ dropdownOn, shadow }: WrapperProps) =>
    !dropdownOn &&
    shadow &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: -28px;
        left: 0;
        width: 100%;
        height: 28px;
        background: ${(props: any) =>
          props.isGrayShadow
            ? "rgba(0, 0, 0, 0) linear-gradient(rgba(244, 244, 244, 0), rgb(244, 244, 244)) repeat scroll 0% 0%;"
            : "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));"};
      }
    `};
  ${Icon} {
    margin-left: 5px;
    vertical-align: middle;
    color: ${theme.blue};
  }
`;

const Content = styled.div`
  margin: 0 -5px;
  padding: 0 5px;
  overflow: hidden;
`;
